import type React from 'react'
import type { JSX } from 'react'
import type { ICard } from '../Card'

import { FallbackImageSizes } from 'atoms/FallbackImage'
import { IconNames } from 'atoms/Icon'
import { TextAligning, TextBox, TextBoxTypes } from 'atoms/TextBox'
import { Colors } from 'atoms/TextBox/TextBox.h'
import { useBooleanState } from 'utils/hooks'
import { COLOR } from 'vars/index'
import { PosterContainerContentSC } from '../base-card'
import {
  CARDS_ASPECT_RATIO,
  CardAnimations,
  CardCorners,
  CardType,
} from '../Card.h'
import { getTransformScale } from '../Templates/MovieCardDefault/lib'
import { stripChannelNumberPrefix, useTvCardBackroundCover } from './lib'
import { TvCardChannelNumberSC, TvSimpleCardSC } from './TVCardDefault.styled'
import { TVCardHoverContent } from './TVCardHoverContent'
import { useTvCardHoverHandlers } from './useTvCardHoverHandlers'

export interface ITVSimpleCardProps extends Omit<ICard, 'children'> {
  children: React.ReactNode
  backgroundColorSelected?: COLOR
  title?: string
  titleFont?: TextBoxTypes
  channelNumber?: number
  covers?: Record<string, any>
}

export const TVSimpleCard = ({
  children,
  posterUrl = '',
  width = '100%',
  aspectRatio = CARDS_ASPECT_RATIO.HORIZONTAL_DEFAULT,
  corners = CardCorners.CORNERS_25,
  backgroundColor = COLOR.SURFACE_DARK_5,
  backgroundColorSelected = COLOR.OVERLAY_PRESSED,
  backgroundColorHovered = COLOR.SURFACE_OPACITY_DARK_70,
  childrenTransition = CardAnimations.FadeIn,
  viewedButtonFavorite = true,
  fallbackImageSize = FallbackImageSizes.S,
  fallbackImageBackground = COLOR.TRANSPARENT,
  selected,
  hidePriceOnHover = false,
  lockedColor: _unused1 = COLOR.TEXT_ICON_PRIMARY,
  overlayOnHoverEnabled: _unused2 = true,
  title = '',
  lockedIcon = IconNames.tvPinLock,
  locked,
  // FP-4298: 16px FontSmBold at every breakpoint (Footnote5/3 scale with viewport)
  titleFont = TextBoxTypes.FontSmBold,
  channelNumber,
  covers,
  scaleOnHover = false,
  aclBlockedBy = false,
  ...rest
}: ITVSimpleCardProps): JSX.Element => {
  const { hoverContentVisible, cardRest, onMouseEnter, onMouseLeave } =
    useTvCardHoverHandlers(rest)
  const [isPosterLoaded, handlePosterLoading, handlePosterError] =
    useBooleanState(true)

  const transformScale = scaleOnHover
    ? getTransformScale(CardType.HORIZONTAL)
    : 1

  const titleComponent = (
    <TextBox
      color={Colors.TEXT_ICON_PRIMARY}
      font={titleFont}
      maxLines={2}
      title={stripChannelNumberPrefix(title, channelNumber)}
      alignment={TextAligning.Center}
    />
  )

  const poster = isPosterLoaded ? posterUrl : ''
  const BackgroundCover =
    locked || poster ? useTvCardBackroundCover(title, covers) : <></>

  const posterContent = (
    <PosterContainerContentSC>
      {!poster && !locked && titleComponent}
      {channelNumber ? (
        <TvCardChannelNumberSC
          color={Colors.TEXT_ICON_SECONDARY}
          font={TextBoxTypes.FontSmBold}
          maxLines={1}
          title={String(channelNumber)}
          alignment={TextAligning.Center}
        />
      ) : (
        <></>
      )}
    </PosterContainerContentSC>
  )

  return (
    <TvSimpleCardSC
      posterUrl={poster}
      width={width}
      aspectRatio={aspectRatio}
      corners={corners}
      backgroundColor={selected ? backgroundColorSelected : backgroundColor}
      backgroundColorHovered={
        !aclBlockedBy ? backgroundColorHovered : COLOR.TRANSPARENT
      }
      childrenTransition={undefined}
      {...cardRest}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      fallbackImageSize={fallbackImageSize}
      fallbackImageBackground={fallbackImageBackground}
      selected={selected}
      hidePriceOnHover={!aclBlockedBy && hidePriceOnHover}
      handlePosterLoading={handlePosterLoading}
      handlePosterError={handlePosterError}
      backgroundCover={BackgroundCover}
      lockedIcon={lockedIcon}
      posterContent={posterContent}
      locked={locked}
      transformScale={transformScale}
      scaleOnHover={scaleOnHover}
      aclBlockedBy={aclBlockedBy}
    >
      <TVCardHoverContent
        favoriteChildren={children}
        hoverContent={!aclBlockedBy && titleComponent}
        viewedButtonFavorite={viewedButtonFavorite}
        aclBlockedBy={aclBlockedBy}
        childrenTransition={childrenTransition}
        hoverTrigger={hoverContentVisible}
      />
    </TvSimpleCardSC>
  )
}
